// oxlint-disable react-hooks/exhaustive-deps

import { useMemo, useState, type ReactNode } from "react";
import type { Column } from "react-table-6";
import { useTranslation } from "react-i18next";
import type { MetricLabel } from "../modules/merchandising/utils/metrics";
import { z } from "zod";

const optionsSchema = z.record(z.record(z.boolean()));
type SelectedColumns = z.infer<typeof optionsSchema>;

const setColumns = (key: string, columns: SelectedColumns[string]) => {
  const serializedOptions = localStorage.getItem("columnOptions")!;
  let options: SelectedColumns = {};
  try {
    options = optionsSchema.parse(JSON.parse(serializedOptions));
  } catch {
    // do nothing
  }
  localStorage.setItem("columnOptions", JSON.stringify({ ...options, [key]: columns }));
};

const getColumns = (key: string): SelectedColumns[string] => {
  const options = localStorage.getItem("columnOptions");
  if (options) {
    try {
      return optionsSchema.parse(JSON.parse(options))[key] ?? {};
    } catch {
      return {};
    }
  }
  return {};
};

export interface SelectorColumn extends Column {
  isStatic?: boolean;
  grouping?: string;
  checked?: boolean;
  toolTip?: MetricLabel | string;
  format?: string;
  onValue?: string;
  offValue?: string;
  Header?: ReactNode | null;
  // Almost always a TSelector; tolerated as a string for the rare interpolated label.
  name?: MetricLabel | string;
}

const useColumnSelector = (columnsFn: () => SelectorColumn[], deps: unknown[], key: string) => {
  const { t } = useTranslation(["metrics"]);
  const [selectedColumns, setSelectedColumns] = useState(() => {
    return key ? getColumns(key) : {};
  });

  return useMemo(() => {
    let columns = columnsFn();
    const columnOptions = columns
      .filter((c) => !c.isStatic)
      .map(({ id, name, checked = true, toolTip, grouping, Header }) => ({
        id: id,
        label:
          // Columns built without a `name` (e.g. the legacy dashboards' `makeColumn`)
          // still carry a translated string `Header`; prefer it over the raw id.
          name == null ?
            typeof Header === "string" ?
              Header
            : (id ?? "")
          : typeof name === "string" ? name
          : t(name),
        checked: selectedColumns[id!] ?? checked,
        toolTip: toolTip == null || typeof toolTip === "string" ? toolTip : t(toolTip),
        grouping,
      }));
    columns = columns.filter((c) => c.isStatic || columnOptions.find((o) => o.id === c.id)?.checked);
    const onColumnChange = (changes: SelectedColumns[string], overwriteSelectedColumns = false) => {
      const newColumns = overwriteSelectedColumns ? { ...changes } : { ...selectedColumns, ...changes };
      if (key) {
        setColumns(key, newColumns);
      }
      setSelectedColumns(newColumns);
    };

    const clearSelectedColumns = () => {
      let updatedColumnOptions: SelectedColumns = {};

      try {
        updatedColumnOptions = optionsSchema.parse(JSON.parse(localStorage.columnOptions));
        // oxlint-disable-next-line typescript/no-dynamic-delete
        delete updatedColumnOptions[key];
      } catch {
        updatedColumnOptions = {};
      }
      localStorage.setItem("columnOptions", JSON.stringify(updatedColumnOptions));
      setSelectedColumns({});
    };
    return [columns, { columnOptions, onColumnChange, clearSelectedColumns }] as const;
  }, [columnsFn, selectedColumns, t, ...deps]);
};

export default useColumnSelector;
